import { resolveEntry, isSpecialValue, FieldMap } from "./tree-walker";

// A node in the data tree. The values are unstructured (form values / GraphQL
// objects), so property values are `unknown`.
interface ObjectWithId {
    id: unknown;
    [propName: string]: unknown;
}
type ObjectWithIdArray = ObjectWithId[];

function isPlainObject(v: unknown): v is Record<string, unknown> {
    return v != null && typeof v === 'object' && !Array.isArray(v);
}

class GQLChanges {
    childTypes: FieldMap;
    constructor(childTypes: FieldMap) {
        this.childTypes = childTypes;
    }
    compareArray(oldData: ObjectWithIdArray, newData: ObjectWithIdArray, path: string): unknown {
        if (!oldData) oldData = [];
        if (!newData) newData = [];
        const oldIndexed: Record<string, ObjectWithId> = {};
        const newIndexed: Record<string, ObjectWithId> = {};
        const allIds: Record<string, boolean> = {};
        oldData.forEach(v => {
            oldIndexed[String(v.id)] = v;
            allIds[String(v.id)] = true;
        });
        let isFiles = false;
        newData.forEach(v => {
            if (isSpecialValue(v)) {
                isFiles = true;
                return;
            }
            allIds[String(v.id)] = true;
            newIndexed[String(v.id)] = v;
        });
        if (isFiles) {
            return newData;
        }
        const { semantics } = resolveEntry(this.childTypes[path]);
        if (semantics === 'connect') {
            const connect: string[] = [];
            const disconnect: string[] = [];
            Object.keys(allIds).forEach(key => {
                if (newIndexed[key] && !oldIndexed[key]) connect.push(key);
                if (oldIndexed[key] && !newIndexed[key]) disconnect.push(key);
            });
            if (!connect.length && !disconnect.length) {
                return null;
            }
            const ret: Record<string, unknown> = {};
            if (connect.length) ret.connect = connect;
            if (disconnect.length) ret.disconnect = disconnect;
            return ret;
        }
        const create: unknown[] = [];
        const update: unknown[] = [];
        const deleteIds: string[] = [];
        let hasUpdate = false;
        Object.keys(allIds).forEach(key => {
            if (!oldIndexed[key]) {
                hasUpdate = true;
                const tc = this.getObjectChanges({ id: null }, newIndexed[key], path) as ObjectWithId;
                tc.creation_id = tc.id;
                delete tc.id;
                create.push(tc);
                return;
            }
            if (!newIndexed[key]) {
                hasUpdate = true;
                deleteIds.push(key);
                return;
            }
            const check = this.getObjectChanges(oldIndexed[key], newIndexed[key], path);
            if (check) {
                hasUpdate = true;
                update.push(check);
            }
        });
        if (!hasUpdate) {
            return null;
        }
        const ret: Record<string, unknown> = {};
        if (create.length) ret.create = create;
        if (update.length) ret.update = update;
        if (deleteIds.length) ret.delete = deleteIds;
        return ret;
    }
    getObjectChanges(oldData?: Record<string, unknown>, newData?: Record<string, unknown>, path?: string): unknown {
        if (oldData == newData) return null;
        if (isSpecialValue(newData)) return newData;
        const changeObject: ObjectWithId = { id: newData.id };
        let seenChange = false;
        const oldKeys = Object.keys(oldData || {});
        const newKeys = Object.keys(newData || {});
        const allIds: Record<string, boolean> = {};
        oldKeys.forEach(k => allIds[k] = true);
        newKeys.forEach(k => allIds[k] = true);
        Object.keys(allIds).forEach(key => {
            const oldRow = oldData ? oldData[key] : null;
            const newRow = newData[key];
            const keyPath = (path ? path + '.' : '') + key;
            const { semantics, projection } = resolveEntry(this.childTypes[keyPath]);
            if (this.applyKeyChange(oldRow, newRow, key, keyPath, semantics, projection, changeObject)) {
                seenChange = true;
            }
        });
        if (!seenChange) return null;
        return changeObject;
    }
    // Decide what, if anything, a single key contributes to the change object.
    // Returns true when a change was recorded.
    private applyKeyChange(
        oldRow: unknown,
        newRow: unknown,
        key: string,
        keyPath: string,
        semantics: 'connect' | 'create' | 'writeOnly' | 'transient' | undefined,
        projection: Record<string, string> | undefined,
        changeObject: ObjectWithId,
    ): boolean {
        const oldNull = oldRow === undefined || oldRow === null;
        const newNull = newRow === undefined || newRow === null;

        // transient/derived values are stripped from the change set
        if (semantics === 'transient') return false;
        // write-only: read is blank, so it is sent iff the new value is set
        // (non-empty); an empty (untouched or set-then-cleared) write is a no-op
        if (semantics === 'writeOnly') {
            if (newRow == null || newRow === '') return false;
            changeObject[key] = newRow;
            return true;
        }

        if (oldNull && newNull) return false;

        // old null/undefined, new set
        if (oldNull && !newNull) {
            if (isSpecialValue(newRow)) {
                changeObject[key] = newRow;
                return true;
            }
            if (Array.isArray(newRow)) {
                const p = this.compareArray([], newRow as ObjectWithIdArray, keyPath);
                if (p) {
                    changeObject[key] = p;
                    return true;
                }
                return false;
            }
            if (isPlainObject(newRow)) {
                if (semantics === 'connect') {
                    changeObject[key] = { connect: newRow.id };
                    return true;
                }
                if (projection) {
                    this.flattenProjection(newRow, projection, changeObject);
                    return true;
                }
                const obj = this.getObjectChanges({ id: null }, newRow, keyPath) as ObjectWithId;
                delete obj.id;
                changeObject[key] = { create: obj };
                return true;
            }
            changeObject[key] = newRow;
            return true;
        }

        // old set, new null/undefined
        if (!oldNull && newNull) {
            if (Array.isArray(oldRow)) {
                const arr = oldRow as ObjectWithIdArray;
                if (semantics === 'connect') {
                    changeObject[key] = { disconnect: [] };
                    arr.forEach(v => (changeObject[key] as { disconnect: unknown[] }).disconnect.push(v.id));
                } else {
                    changeObject[key] = { delete: [] };
                    arr.forEach(v => (changeObject[key] as { delete: unknown[] }).delete.push(v.id));
                }
                return true;
            }
            if (isPlainObject(oldRow)) {
                if (semantics === 'connect') changeObject[key] = { disconnect: true };
                else changeObject[key] = { delete: true };
                return true;
            }
            changeObject[key] = null;
            return true;
        }

        // both set
        if (oldRow === newRow) return false;
        if (Array.isArray(newRow)) {
            const p = this.compareArray(oldRow as ObjectWithIdArray, newRow as ObjectWithIdArray, keyPath);
            if (p) {
                changeObject[key] = p;
                return true;
            }
            return false;
        }
        if (isPlainObject(newRow)) {
            if (newRow.id == null) {
                changeObject[key] = { disconnect: true };
                return true;
            }
            // A single-object relation is addressed by ID only: a change of ID is
            // the only change; there is no recursion into the related object.
            if (semantics === 'connect') {
                const oldId = isPlainObject(oldRow) ? oldRow.id : undefined;
                if (newRow.id != oldId) {
                    changeObject[key] = { connect: newRow.id };
                    return true;
                }
                return false;
            }
            // Owned object (create, with or without projection): a generic object
            // diff. The projection only applies on create (flattened onto the
            // parent), so set->set uses the plain sub-diff.
            const temp = this.getObjectChanges(isPlainObject(oldRow) ? oldRow : undefined, newRow, keyPath);
            if (!temp) return false;
            changeObject[key] = { update: temp };
            return true;
        }
        // scalar (or scalar<->object mixed)
        changeObject[key] = newRow;
        return true;
    }
    // Flatten a projected owned object onto its parent, matching the legacy
    // `campaignComponent` branch: `.id` sources require the source object to be
    // present; scalar sources are set when truthy; `qty` is always set.
    private flattenProjection(newRow: Record<string, unknown>, projection: Record<string, string>, changeObject: ObjectWithId): void {
        for (const [inputField, sourcePath] of Object.entries(projection)) {
            const parts = sourcePath.split('.');
            if (parts.length > 1) {
                let cur: unknown = newRow;
                for (let i = 0; i < parts.length - 1; i++) {
                    cur = isPlainObject(cur) ? cur[parts[i]] : undefined;
                }
                if (cur) changeObject[inputField] = isPlainObject(cur) ? cur[parts[parts.length - 1]] : undefined;
            } else {
                const val = newRow[sourcePath];
                if (inputField === 'qty') changeObject[inputField] = parseInt(String(val));
                else if (val) changeObject[inputField] = val;
            }
        }
    }
    compare(oldData: ObjectWithId | ObjectWithIdArray, newData: ObjectWithId | ObjectWithIdArray): unknown {
        if (Array.isArray(oldData) || Array.isArray(newData)) {
            return this.compareArray(oldData as ObjectWithIdArray, newData as ObjectWithIdArray, '');
        }
        return this.getObjectChanges(oldData as ObjectWithId, newData as ObjectWithId, '');
    }
    clone(obj: unknown, path = ''): unknown {
        if (obj == null) return obj;
        if (Array.isArray(obj)) {
            return obj.map((v, i) => this.clone(v, path + '.' + i));
        }
        if (isPlainObject(obj)) {
            const result: Record<string, unknown> = {};
            for (const key of Object.keys(obj)) {
                if (key === '__cloned') continue;
                const value = obj[key];
                if (value instanceof File) {
                    result[key] = value;
                    result['clone_id'] = `${path}.${key}`;
                } else {
                    result[key] = this.clone(value, path + '.' + key);
                }
            }
            result['__cloned'] = true;
            return result;
        }
        return obj;
    }
}

export default GQLChanges;
